/**
 * Mock Teaching Agent Service for Development
 *
 * Provides simulated responses for testing the teaching agent UI
 * without requiring the backend service to be running.
 */

import {
  ChatMessage,
  VideoContent,
  BookContent,
  SocraticQuestion,
  Celebration,
  StreamCallbacks,
  SessionInfo,
} from './teachingAgent';

// Mock delay to simulate network latency
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Mock Socratic responses
const MOCK_RESPONSES = [
  {
    trigger: /help|stuck|confused/i,
    response: "I can see you're working through a challenge. Before I give you any hints, let me ask: **what have you tried so far?** Understanding your approach helps me guide you better.",
    question: {
      question: "What specific part is causing you the most difficulty?",
      type: 'clarifying' as const,
      relatedConcept: 'debugging',
    },
  },
  {
    trigger: /loop|for|while|iterate/i,
    response: "Loops are fundamental to programming! Let's think about this together. **What do you think happens in each iteration of your loop?** Try tracing through it mentally with a simple example.",
    video: {
      videoId: 'loops-101',
      title: 'Understanding Loops in Python',
      url: 'https://example.com/video/loops',
      reason: 'This video explains loop mechanics with visual examples',
    },
  },
  {
    trigger: /function|def|return/i,
    response: "Functions are like recipes - they take ingredients (parameters) and produce a result (return value). **Can you describe in plain English what you want your function to do?**",
    book: {
      bookId: 'python-basics',
      chapter: 'Chapter 4: Functions',
      excerpt: '## Defining Functions\n\nA function is a reusable block of code that performs a specific task. When you **define** a function, you specify:\n\n1. The function name\n2. Parameters it accepts\n3. What it returns\n\n```python\ndef greet(name):\n    return f"Hello, {name}!"\n```',
      citation: 'Python Fundamentals, 3rd Edition',
      pageReference: '87-92',
    },
  },
  {
    trigger: /error|exception|bug|wrong/i,
    response: "Errors are actually helpful - they're telling us something! **What does the error message say?** Let's decode it together. Often the error points directly to what needs fixing.",
    question: {
      question: "Can you share the exact error message you're seeing?",
      type: 'probing' as const,
      relatedConcept: 'error handling',
    },
  },
  {
    trigger: /list|array|collection/i,
    response: "Lists are one of Python's most versatile data structures. Think of them as ordered containers. **What operation are you trying to perform on your list?** Knowing that will help me guide you to the right approach.",
  },
  {
    trigger: /thanks|thank you|got it|understand/i,
    response: "Excellent progress! You're building a strong foundation. Keep experimenting and asking questions - that's how real learning happens!",
    celebration: {
      achievement: 'Concept Mastered!',
      encouragement: "You've demonstrated great problem-solving skills. Keep up the momentum!",
      nextSuggestion: 'Try applying this concept to the next task in your lab.',
    },
  },
];

// Default response when no trigger matches
const DEFAULT_RESPONSE = {
  response: "That's an interesting question! Let me turn it around: **what's your current understanding of this concept?** Sometimes explaining what we know reveals what we need to learn.",
  question: {
    question: "Can you tell me more about what you're trying to accomplish?",
    type: 'clarifying' as const,
  },
};

// Mock session storage
const mockSessions = new Map<string, { messages: ChatMessage[]; createdAt: Date }>();

/**
 * Simulate streaming response with mock data
 */
export async function mockStreamChat(
  message: string,
  callbacks: StreamCallbacks,
  sessionId: string
): Promise<void> {
  callbacks.onStart?.();

  // Find matching response
  let matchedResponse = MOCK_RESPONSES.find(r => r.trigger.test(message));
  if (!matchedResponse) {
    matchedResponse = DEFAULT_RESPONSE as typeof MOCK_RESPONSES[0];
  }

  // Simulate thinking delay
  await delay(800);

  // Stream the response character by character
  const response = matchedResponse.response;
  for (let i = 0; i < response.length; i++) {
    callbacks.onContent?.(response[i]);
    await delay(15 + Math.random() * 20); // Variable typing speed
  }

  // Send additional content types
  await delay(300);

  if (matchedResponse.video) {
    callbacks.onVideo?.(matchedResponse.video);
  }

  if (matchedResponse.book) {
    callbacks.onBook?.(matchedResponse.book);
  }

  if (matchedResponse.question) {
    callbacks.onQuestion?.(matchedResponse.question);
  }

  if (matchedResponse.celebration) {
    callbacks.onCelebration?.(matchedResponse.celebration);
  }

  callbacks.onEnd?.();
}

/**
 * Create a mock session
 */
export async function mockCreateSession(options: {
  labId?: string;
  persistenceContext?: string;
}): Promise<SessionInfo> {
  await delay(200);

  const sessionId = `mock-session-${Date.now()}`;
  mockSessions.set(sessionId, {
    messages: [],
    createdAt: new Date(),
  });

  return {
    sessionId,
    labId: options.labId,
    createdAt: new Date(),
    messageCount: 0,
  };
}

/**
 * Get mock session
 */
export async function mockGetSession(sessionId: string): Promise<SessionInfo | null> {
  await delay(100);

  const session = mockSessions.get(sessionId);
  if (!session) return null;

  return {
    sessionId,
    studentId: 'mock-student',
    createdAt: session.createdAt,
    messageCount: session.messages.length,
    status: 'active',
  };
}

/**
 * Get mock history
 */
export async function mockGetHistory(sessionId: string): Promise<ChatMessage[]> {
  await delay(100);

  const session = mockSessions.get(sessionId);
  return session?.messages || [];
}

/**
 * End mock session
 */
export async function mockEndSession(sessionId: string): Promise<void> {
  await delay(100);
  mockSessions.delete(sessionId);
}

/**
 * Mock interrupt (no-op in mock mode)
 */
export async function mockInterrupt(): Promise<void> {
  await delay(50);
}

export const mockTeachingAgentAPI = {
  createSession: mockCreateSession,
  getSession: mockGetSession,
  getHistory: mockGetHistory,
  listActiveSessions: async (): Promise<[]> => [],
  endSession: mockEndSession,
  interrupt: mockInterrupt,
  sendMessageStreaming: async (
    options: { sessionId: string; message: string },
    callbacks: StreamCallbacks,
    _signal?: AbortSignal
  ) => mockStreamChat(options.message, callbacks, options.sessionId),
};

export default mockTeachingAgentAPI;
